import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Search, Sparkles, FileText, Users, Building, Mail, Phone,
  Zap, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SearchPage = () => {
  const { user } = useAuth();
  const t = useTranslation(user?.language || 'ja');
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('text');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerms, setSearchTerms] = useState('');

  const handleSearch = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/search`, {
        params: { q: query, search_type: searchType },
        withCredentials: true,
      });
      setResults(data.results);
      setSearchTerms(data.search_terms);
    } catch (e) {
      console.error('Search failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(user?.language === 'en' ? 'en-US' : 'ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const totalResults = results 
    ? (results.contacts?.length || 0) + (results.files?.length || 0)
    : 0;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="search-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-glow">{t.search.title}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {user?.language === 'ja' ? '連絡先・ファイルを横断検索' : 'Search across contacts and files'}
        </p>
      </div>

      {/* Search Bar */}
      <Card className="card-cyber border-glow">
        <CardContent className="p-4 sm:p-6">
          <div className="flex gap-2 sm:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t.search.placeholder}
                className="pl-10 sm:pl-12 h-10 sm:h-12 text-sm sm:text-lg input-cyber"
                data-testid="search-input"
              />
            </div>
            <Button
              onClick={handleSearch}
              className="h-10 sm:h-12 px-4 sm:px-8 btn-primary"
              disabled={loading}
              data-testid="search-button"
            >
              {loading ? (
                <Clock className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
            </Button>
          </div>
          
          {/* Search Type Toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-4">
            <span className="text-sm text-muted-foreground">
              {user?.language === 'ja' ? '検索モード:' : 'Search mode:'}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={searchType === 'text' ? 'default' : 'outline'}
                onClick={() => setSearchType('text')}
                className={searchType === 'text' ? 'btn-primary' : 'btn-secondary'}
                data-testid="text-search-btn"
              >
                <Search className="w-4 h-4 mr-1" />
                {t.search.textSearch}
              </Button>
              <Button
                size="sm"
                variant={searchType === 'ai' ? 'default' : 'outline'}
                onClick={() => setSearchType('ai')}
                className={searchType === 'ai' ? 'btn-primary' : 'btn-secondary'}
                data-testid="ai-search-btn"
              >
                <Sparkles className="w-4 h-4 mr-1" />
                {t.search.aiSearch}
              </Button>
            </div>
          </div>
          
          {searchType === 'ai' && searchTerms && searchTerms !== query && (
            <div className="mt-4 p-3 bg-primary/10 rounded-sm border border-primary/20">
              <div className="flex items-center gap-2 text-sm">
                <Zap className="w-4 h-4 text-primary" />
                <span className="text-muted-foreground">
                  {user?.language === 'ja' ? 'AI検索キーワード:' : 'AI search terms:'}
                </span>
                <span className="text-primary font-mono">{searchTerms}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <Card className="card-cyber">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>{t.search.results}</span>
              <Badge variant="outline" className="font-mono">
                {totalResults} {user?.language === 'ja' ? '件' : 'results'}
              </Badge>
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-0">
            {totalResults === 0 ? (
              <div className="p-12 text-center">
                <Search className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground">{t.search.noResults}</p>
              </div>
            ) : (
              <Tabs defaultValue="contacts" className="w-full">
                <TabsList className="px-4 bg-transparent border-b border-border rounded-none h-auto justify-start w-full">
                  <TabsTrigger 
                    value="contacts" 
                    className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                  >
                    <Users className="w-4 h-4 mr-1" />
                    {t.nav.contacts} ({results.contacts?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger 
                    value="files" 
                    className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    {t.nav.files} ({results.files?.length || 0})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="contacts" className="m-0 p-4">
                  <ScrollArea className="h-[400px]">
                    {results.contacts?.length > 0 ? (
                      <div className="space-y-3">
                        {results.contacts.map((contact) => (
                          <div
                            key={contact.id}
                            className="p-4 rounded-sm bg-secondary/50 hover:bg-secondary transition-colors cursor-pointer"
                            data-testid={`search-contact-${contact.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium">{contact.name}</p>
                                {contact.company && (
                                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                    <Building className="w-3 h-3" />
                                    {contact.company}
                                  </p>
                                )}
                              </div>
                              <span className={contact.status === 'active' ? 'badge-active' : 'badge-inactive'}>
                                {contact.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                              {contact.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {contact.email}
                                </span>
                              )}
                              {contact.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {contact.phone}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>{t.contacts.noContacts}</p>
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
                
                <TabsContent value="files" className="m-0 p-4">
                  <ScrollArea className="h-[400px]">
                    {results.files?.length > 0 ? (
                      <div className="space-y-3">
                        {results.files.map((file) => (
                          <div
                            key={file.id}
                            className="p-4 rounded-sm bg-secondary/50 hover:bg-secondary transition-colors cursor-pointer"
                            data-testid={`search-file-${file.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <FileText className="w-5 h-5 text-primary mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium">{file.title}</p>
                                <p className="text-sm text-muted-foreground truncate">
                                  {file.original_filename}
                                </p>
                                {file.ocr_text && (
                                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                                    {file.ocr_text.substring(0, 200)}...
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground font-mono mt-2">
                                  {formatDate(file.created_at)}
                                </p>
                              </div>
                              {file.is_business_card && (
                                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                                  {t.files.businessCard}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>{t.files.noFiles}</p>
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchPage;
